import React from "react";
import { Box, IconButton, LinearProgress, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import {
  getQueueStatusLabel,
  getTaskStatusLabel,
  resolveCurrentTask,
} from "./TaskQueueStore";
import { useStatusPalette } from "../../../utils/statusColors";

function buildConnConfigs(p) {
  return {
    live:          { label: "Connected", ...p.greenAlt },
    reconciling:   { label: "Syncing", ...p.blueAlt },
    reconnecting:  { label: "Reconnecting", ...p.amberAlt },
    polling:       { label: "Polling", ...p.blueAlt },
  };
}

function buildQueueStatusStyles(p) {
  return {
    ACTIVE:    p.blueAlt,
    PAUSED:    p.amberAlt,
    COMPLETED: p.greenAlt,
    CANCELLED: p.zincAlt,
  };
}

function StatusPill({ label, color, bg, border }) {
  return (
    <Box
      sx={{
        px: 1,
        py: "3px",
        borderRadius: "6px",
        border: `1px solid ${border}`,
        backgroundColor: bg,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color, lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function TaskQueueHeader({
  queue,
  progress,
  loading,
  connectionState,
  expanded,
  onToggleExpanded,
  onRefresh,
  onClose,
}) {
  const _sp = useStatusPalette();
  const QUEUE_STATUS_STYLES = buildQueueStatusStyles(_sp);
  const CONN_CONFIGS = buildConnConfigs(_sp);
  const queueStatus = String(queue?.status || "").toUpperCase();
  const queueStatusLabel = getQueueStatusLabel(queueStatus);
  const queueStyle = QUEUE_STATUS_STYLES[queueStatus] || QUEUE_STATUS_STYLES.CANCELLED;
  const connKey = String(connectionState || "idle").toLowerCase();
  const connCfg = CONN_CONFIGS[connKey] || null;
  const currentTask = resolveCurrentTask(queue);
  const currentLabel = currentTask
    ? `${currentTask.title || "Task"} — ${getTaskStatusLabel(currentTask.status)}`
    : null;
  const pct = progress.total > 0 ? Math.round((progress.resolved / progress.total) * 100) : 0;

  return (
    <Box sx={{ borderBottom: "1px solid #e5e7eb" }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "text.primary",
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {queue?.goal || "Task Queue"}
            </Typography>
            {currentLabel && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6b7280",
                  mt: 0.5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {currentLabel}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
            {connCfg && <StatusPill {...connCfg} />}
            <StatusPill label={queueStatusLabel} {...queueStyle} />
            <IconButton
              onClick={onToggleExpanded}
              size="small"
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": { backgroundColor: "action.hover", borderColor: "text.disabled" },
              }}
            >
              {expanded ? <CloseFullscreenIcon sx={{ fontSize: 14 }} /> : <OpenInFullIcon sx={{ fontSize: 14 }} />}
            </IconButton>
            <IconButton
              onClick={onRefresh}
              size="small"
              disabled={loading}
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": { backgroundColor: "action.hover", borderColor: "text.disabled" },
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 14,
                  ...(loading && {
                    animation: "tqSpin 1s linear infinite",
                    "@keyframes tqSpin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }),
                }}
              />
            </IconButton>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "divider",
                color: "text.disabled",
                "&:hover": { backgroundColor: "action.hover", borderColor: "text.disabled" },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mt: 1.25 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "text.primary" }}>
              {progress.resolved}/{progress.total} completed
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
              {pct}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 6,
              borderRadius: 99,
              backgroundColor: "action.hover",
              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                background: "linear-gradient(90deg, #3b82f6, #60a5fa)",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
